import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  LabelList, ReferenceLine,
} from "recharts";
import type { UserRowV2 } from "./sql";
import { STAGES } from "./helpers";
import { pctVal } from "../../lib/funnel/helpers";
import { PRIMARY, STEEL, MUTED, FG1 } from "../../lib/funnel/theme";
import { LegendDot } from "../../lib/funnel/charts";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

type LabelProps = {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: unknown;
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const renderDemoLabel = (format: (n: number) => string) => (props: LabelProps) => {
  const x = num(props.x), y = num(props.y), w = num(props.width), v = num(props.value);
  if (x == null || y == null || w == null || v == null) return null;
  if (v <= 0) return null;
  return (
    <text x={x + w / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={PRIMARY}>
      {format(v)}
    </text>
  );
};

const renderSelfLabel = (format: (n: number) => string) => (props: LabelProps) => {
  const x = num(props.x), y = num(props.y), w = num(props.width), v = num(props.value);
  if (x == null || y == null || w == null || v == null || v >= 0) return null;
  return (
    <text x={x + w / 2} y={y + 14} textAnchor="middle" fontSize={11} fontWeight={600} fill={STEEL}>
      {format(Math.abs(v))}
    </text>
  );
};

function wrapLines(text: string, maxChars: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (test.length > maxChars && current) { lines.push(current); current = word; }
    else { current = test; }
  }
  if (current) lines.push(current);
  return lines;
}

type TickProps = { x?: number | string; y?: number | string; payload?: { value: string } };

function WrappedTick({ x, y, payload }: TickProps) {
  const xn = typeof x === "number" ? x : Number(x) || 0;
  const yn = typeof y === "number" ? y : Number(y) || 0;
  const label = payload?.value ?? "";
  const lines = wrapLines(label, 14);
  return (
    <g>
      <text x={xn} y={yn + 10} textAnchor="middle" fontSize={11} fill={FG1}>
        {lines.map((line, i) => (
          <tspan key={i} x={xn} dy={i === 0 ? 0 : 14}>{line}</tspan>
        ))}
      </text>
    </g>
  );
}

type StepDatum = {
  step: string;
  demoVal: number;
  selfVal: number;
  demoPct: number;
  selfPct: number;
  demoCount: number;
  selfCount: number;
};

export function ButterflyChartV2({
  users,
  pagePills,
}: {
  users: UserRowV2[];
  pagePills?: FilterPillSpec[];
}) {
  const [showAbs, setShowAbs] = useState(false);
  const demo = users.filter((u) => u.had_demo);
  const self = users.filter((u) => !u.had_demo);
  const demoTotal = demo.length;
  const selfTotal = self.length;

  const mkStep = (label: string, pred: (u: UserRowV2) => boolean): StepDatum => {
    const dCount = demo.filter(pred).length;
    const sCount = self.filter(pred).length;
    const dPct = pctVal(dCount, demoTotal);
    const sPct = pctVal(sCount, selfTotal);
    return {
      step: label,
      demoVal: showAbs ? dCount : dPct,
      selfVal: showAbs ? -sCount : -sPct,
      demoPct: dPct,
      selfPct: -sPct,
      demoCount: dCount,
      selfCount: sCount,
    };
  };

  const data: StepDatum[] = [
    {
      step: "Signed up",
      demoVal: showAbs ? demoTotal : 100,
      selfVal: showAbs ? -selfTotal : -100,
      demoPct: 100,
      selfPct: -100,
      demoCount: demoTotal,
      selfCount: selfTotal,
    },
    ...STAGES.map((s) => mkStep(s.label, (u) => s.dateOf(u) != null)),
  ];

  const maxMag = Math.max(...data.map((d) => Math.max(Math.abs(d.demoVal), Math.abs(d.selfVal))), showAbs ? 0 : 100);
  const niceStep = (range: number): number => {
    if (range <= 0) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(range / 3)));
    const n = (range / 3) / mag;
    if (n < 1.5) return 1 * mag;
    if (n < 3) return 2 * mag;
    if (n < 7) return 5 * mag;
    return 10 * mag;
  };
  const step = niceStep(maxMag);
  const ticksPerSide = Math.max(1, Math.ceil(maxMag / step));
  const yMax = step * ticksPerSide;
  const yTicks: number[] = [];
  for (let i = -ticksPerSide; i <= ticksPerSide; i++) yTicks.push(i * step);

  const fmtVal = (n: number): string => (showAbs ? Math.round(n).toLocaleString() : `${n.toFixed(1)}%`);
  const fmtAxis = (n: number): string => (showAbs ? Math.round(n).toLocaleString() : `${n.toFixed(0)}%`);

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    { kind: "mode", label: showAbs ? "Values: counts" : "Values: percentages" },
  ];

  return (
    <section className="card">
      <SectionCardHeader
        title="Activation funnel by sign-up path"
        subtitle="Demo path (above zero) and self-serve (below) compared on the same denominator: signed-up users in cohort. Each step is a lifetime hit, not bound to the 90-day window."
        filterPills={pills}
        headerExtra={
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap" }}>
            <LegendDot color={PRIMARY} label={`Demo path (n=${demoTotal})`} />
            <LegendDot color={STEEL} label={`Self-serve (n=${selfTotal})`} />
            <div
              role="group"
              aria-label="Value mode"
              style={{
                display: "inline-flex",
                border: `1px solid ${MUTED}`,
                borderRadius: 6,
                overflow: "hidden",
                fontSize: 11,
              }}
            >
              {([
                { key: false, label: "%" },
                { key: true, label: "#" },
              ] as const).map((opt) => {
                const active = showAbs === opt.key;
                return (
                  <button
                    key={String(opt.key)}
                    type="button"
                    onClick={() => setShowAbs(opt.key)}
                    style={{
                      padding: "3px 10px",
                      background: active ? PRIMARY : "transparent",
                      color: active ? "#fff" : FG1,
                      border: "none",
                      cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        }
      />
      <div className="card-body">
        <ResponsiveContainer width="100%" height={520}>
          <BarChart data={data} margin={{ top: 28, right: 16, left: 16, bottom: 8 }} stackOffset="sign">
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(0,0,0,0.15)" vertical={false} />
            <XAxis dataKey="step" tick={WrappedTick} interval={0} height={64} />
            <YAxis
              domain={[-yMax, yMax]}
              ticks={yTicks}
              padding={{ top: 0, bottom: 20 }}
              tickFormatter={(v) => fmtAxis(Math.abs(Number(v)))}
              tick={{ fontSize: 10, fill: MUTED }}
              width={showAbs ? 56 : 42}
            />
            <ReferenceLine y={0} stroke={FG1} strokeWidth={1} />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
              formatter={(_v, name, item) => {
                const row = item?.payload as StepDatum | undefined;
                const isDemo = name === "demoVal";
                const count = (isDemo ? row?.demoCount : row?.selfCount) ?? 0;
                const pct = Math.abs(isDemo ? (row?.demoPct ?? 0) : (row?.selfPct ?? 0));
                const total = isDemo ? demoTotal : selfTotal;
                return [`${pct.toFixed(1)}% (${count.toLocaleString()}/${total.toLocaleString()})`, isDemo ? "Demo" : "Self"] as [string, string];
              }}
            />
            <Bar dataKey="demoVal" name="demoVal" stackId="path" fill={PRIMARY} radius={[4, 4, 0, 0]} isAnimationActive={false}>
              <LabelList dataKey="demoVal" content={renderDemoLabel(fmtVal)} />
            </Bar>
            <Bar dataKey="selfVal" name="selfVal" stackId="path" fill={STEEL} radius={[4, 4, 0, 0]} isAnimationActive={false}>
              <LabelList dataKey="selfVal" content={renderSelfLabel(fmtVal)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
